import type { GraphQLEnumDescriptor } from "./graphql-enum"

// Input objects and `where` filters. The generated schema describes an input type only with what the document builder needs
// at runtime: its GraphQL name, its enum fields (numbers in application code, names on the wire) and its nested input objects.

/** One field of an input object that needs runtime knowledge. It applies to a list of values as well. */
export type GraphQLInputField = { readonly enum: GraphQLEnumDescriptor } | { readonly input: () => GraphQLInputShape }

/** The fields of an input object that need runtime knowledge. Every other field is written as it is. */
export interface GraphQLInputShape {
  readonly fields?: Readonly<Record<string, GraphQLInputField>>
}

/** An input object type of the schema, such as `SharedStockItemFilterInput`, or the `where` input of a node type. */
export interface GraphQLInputDescriptor extends GraphQLInputShape {
  readonly name: string
}

// The `where` filter of a [UseFiltering] field: the operations that Hot Chocolate's default filter convention defines per kind of
// field. The generated schema declares one `where` type per object, with a field per filterable property.

/** A string field. `and` and `or` combine operations on the same field. */
export interface GraphQLStringFilter {
  readonly and?: ReadonlyArray<GraphQLStringFilter>
  readonly or?: ReadonlyArray<GraphQLStringFilter>
  readonly eq?: string | null
  readonly neq?: string | null
  readonly contains?: string
  readonly ncontains?: string
  readonly in?: ReadonlyArray<string>
  readonly nin?: ReadonlyArray<string>
  readonly startsWith?: string
  readonly nstartsWith?: string
  readonly endsWith?: string
  readonly nendsWith?: string
}

/** A number, date, time span or uuid field. Dates, times and uuids are strings. */
export interface GraphQLComparableFilter<TValue> {
  readonly eq?: TValue | null
  readonly neq?: TValue | null
  readonly in?: ReadonlyArray<TValue>
  readonly nin?: ReadonlyArray<TValue>
  readonly gt?: TValue
  readonly ngt?: TValue
  readonly gte?: TValue
  readonly ngte?: TValue
  readonly lt?: TValue
  readonly nlt?: TValue
  readonly lte?: TValue
  readonly nlte?: TValue
}

export interface GraphQLBooleanFilter {
  readonly eq?: boolean | null
  readonly neq?: boolean | null
}

/** An enum field. The values are the numbers of the enum, as everywhere in application code. */
export interface GraphQLEnumFilter<TValue> {
  readonly eq?: TValue | null
  readonly neq?: TValue | null
  readonly in?: ReadonlyArray<TValue>
  readonly nin?: ReadonlyArray<TValue>
}

/** A list field: `some`, `all` and `none` take the filter of its element, `any` asks whether the list has items. */
export interface GraphQLListFilter<TElementFilter> {
  readonly some?: TElementFilter
  readonly all?: TElementFilter
  readonly none?: TElementFilter
  readonly any?: boolean
}

// Descriptor helpers, used by the generated schema.

/** The `where` input of a node type. Its `and` and `or` take the same input again. */
export function defineGraphQLFilter(
  name: string,
  fields: Readonly<Record<string, GraphQLInputField>> = {}
): GraphQLInputDescriptor {
  const descriptor: GraphQLInputDescriptor = {
    name,
    fields: { ...fields, and: { input: () => descriptor }, or: { input: () => descriptor } },
  }
  return descriptor
}

/** A filter on an enum field: every operation takes enum values. */
export function filterEnum(enumeration: GraphQLEnumDescriptor): GraphQLInputField {
  const field = { enum: enumeration }
  const shape: GraphQLInputShape = { fields: { eq: field, neq: field, in: field, nin: field } }
  return { input: () => shape }
}

/** A filter on a list field: `some`, `all` and `none` take the filter of the element. */
export function filterList(element: GraphQLInputField): GraphQLInputField {
  const shape: GraphQLInputShape = { fields: { some: element, all: element, none: element } }
  return { input: () => shape }
}
